import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const GRADING_FINISHED_TASK_CLASS = 'GradingFinishedPendingTask';

/**
 * What triggered the grading run — drives the wording of the announcement.
 *  manual                  — the agent ran the grading tool on the user's request (default)
 *  new_submissions         — re-uploaded/new submissions were graded for the first time
 *  master_solution_update  — existing submissions were re-graded after a new master solution
 *  grading_scheme_update   — existing submissions were re-graded after a new grading scheme
 *  manual_grading_insight  — a teacher's manual score correction for one submission was folded into the
 *                            grading scheme, and the other submissions of that task were re-graded to match
 */
export type GradingFinishedCause =
  | 'manual'
  | 'new_submissions'
  | 'master_solution_update'
  | 'grading_scheme_update'
  | 'manual_grading_insight';

export interface GradingFinishedTaskOptions {
  taskNumbers: string[];
  gradedCount: number;
  uncertaintiesTotal: number;
  shouldSuggestCanvas: boolean;
  /**
   * Every change this one re-grade covers. A master solution AND a grading scheme from the same upload
   * share one re-grade, and the teacher has to hear about both — so this is a list, not a single value.
   * Defaults to `['manual']`.
   */
  causes?: GradingFinishedCause[];
  /**
   * How many of `gradedCount` were already `isApproved` before this re-grade (and therefore had that
   * approval reset by it) — only meaningful for `manual_grading_insight`, since that is the one cause
   * that deliberately re-grades previously-approved submissions too. Omit/0 otherwise.
   */
  previouslyApprovedCount?: number;
}

/**
 * Registered entity so the polymorphic examState.pendingTasks array can be hydrated (getByID /
 * getEntityByID construct each element by its `_class`). The raw document is still pushed via
 * createGradingFinishedPendingTask below; this just gives it a constructor in the metaDataStore.
 */
@Entity()
export class GradingFinishedPendingTask extends PendingTask {
  static override _class = GRADING_FINISHED_TASK_CLASS;

  @Field(() => String)
  priority: Maybe<string>;

  @Field(() => [String])
  taskNumbers: Maybe<string[]>;

  @Field(() => Float)
  gradedCount: Maybe<number>;

  @Field(() => Float)
  uncertaintiesTotal: Maybe<number>;

  @Field(() => Boolean)
  shouldSuggestCanvas: Maybe<boolean>;

  @Field(() => [String])
  causes: Maybe<GradingFinishedCause[]>;

  @Field(() => Float)
  previouslyApprovedCount: Maybe<number>;
}

/** Creates the raw document pushed to examState.pendingTasks in MongoDB. */
export function createGradingFinishedPendingTask(options: GradingFinishedTaskOptions): Record<string, unknown> {
  return {
    _class: GRADING_FINISHED_TASK_CLASS,
    priority: 'workflow',
    taskNumbers: options.taskNumbers,
    gradedCount: options.gradedCount,
    uncertaintiesTotal: options.uncertaintiesTotal,
    shouldSuggestCanvas: options.shouldSuggestCanvas,
    causes: options.causes?.length ? options.causes : ['manual'],
    previouslyApprovedCount: options.previouslyApprovedCount ?? 0,
  };
}

/**
 * What the re-grade was based on, as a genitive phrase for the "Aufgrund …" frame below. Key order is
 * the order they are named in: the queue they come from is a set and carries no meaningful order.
 */
const CAUSE_BASIS_PHRASES: Partial<Record<GradingFinishedCause, string>> = {
  master_solution_update: 'der aktualisierten Musterlösung',
  grading_scheme_update: 'des aktualisierten Bewertungsschemas',
  manual_grading_insight:
    'einer manuellen Korrektur, deren Erkenntnis auf die übrigen Abgaben dieser Aufgabe übertragen wurde',
};

registerPendingTaskDescBuilder(GRADING_FINISHED_TASK_CLASS, task => {
  const t = task as unknown as Record<string, unknown>;
  const taskNumbers = Array.isArray(t.taskNumbers)
    ? (t.taskNumbers as string[]).join(', ')
    : String(t.taskNumbers ?? '');
  const gradedCount = Number(t.gradedCount ?? 0);
  const uncertaintiesTotal = Number(t.uncertaintiesTotal ?? 0);
  const shouldSuggestCanvas = Boolean(t.shouldSuggestCanvas);
  const previouslyApprovedCount = Number(t.previouslyApprovedCount ?? 0);

  const causes = (Array.isArray(t.causes) ? (t.causes as GradingFinishedCause[]) : []).filter(Boolean);

  const uncertaintyNote = shouldSuggestCanvas
    ? `Es gibt ${uncertaintiesTotal} offene Unsicherheiten. Biete dem Nutzer aktiv an, den Korrektur-Canvas zu öffnen.`
    : 'Es gibt keine offenen Unsicherheiten.';

  // One re-grade can cover several changes at once (master solution AND grading scheme from the same
  // upload), and naming all of them is the whole point — otherwise the teacher hears about one file and
  // is left wondering whether the other one was used.
  //
  // `new_submissions` MIXES WITH THEM, which is why the two frames are built separately and joined rather
  // than chosen between. It used to be asserted here that it could not: a re-grade wave was whatever the
  // exam-level ledger held when it was drained, and the new-submission path drained its own. A wave is a
  // BATCH now and grading batches per exam, so an upload replacing a master solution while three new
  // submissions are still extracting puts both causes in one announcement — and the single "neu bewertet"
  // frame then told the teacher that three FIRST gradings had been re-gradings.
  //
  // Two sentences rather than a merged one because the two halves describe different submissions, and the
  // announcement carries only a combined `gradedCount`: saying which is which would need the graded set
  // partitioned by cause, and neither sentence claims a number it does not have.
  const basis = (Object.keys(CAUSE_BASIS_PHRASES) as GradingFinishedCause[])
    .filter(cause => causes.includes(cause))
    .map(cause => CAUSE_BASIS_PHRASES[cause]);
  const regraded = basis.length
    ? `Aufgrund ${basis.join(' und ')} wurden die betroffenen Abgaben im Hintergrund automatisch neu bewertet.`
    : null;
  const firstGraded = causes.includes('new_submissions')
    ? 'Die neu hochgeladenen Schülerabgaben wurden im Hintergrund automatisch bewertet.'
    : null;
  const intro =
    [regraded, firstGraded].filter(Boolean).join(' ') || 'Die KI-Bewertung wurde im Hintergrund abgeschlossen.';

  const previouslyApprovedNote =
    causes.includes('manual_grading_insight') && previouslyApprovedCount > 0
      ? `\n- Davon waren ${previouslyApprovedCount} Abgabe(n) zuvor bereits von der Lehrkraft freigegeben ("genehmigt") — diese Freigabe wurde durch die Neubewertung zurückgesetzt und sollte erneut geprüft werden.`
      : '';

  return `### WORKFLOW TASK: Bewertung abgeschlossen [${GRADING_FINISHED_TASK_CLASS}]
${intro}
- Bewertete Aufgaben: ${taskNumbers}
- Bewertete Abgaben: ${gradedCount}${previouslyApprovedNote}
- ${uncertaintyNote}

Informiere den Nutzer über das Ergebnis. Wenn es Unsicherheiten gibt, öffne proaktiv den Korrektur-Canvas, damit der Nutzer die Unsicherheiten überprüfen und manuell korrigieren kann.`;
});
